/* eslint-disable @ngrx/prefer-effect-callback-in-block-statement */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';

import { AppRoutePath } from '@cdba/app-route-path.enum';

import { SearchService } from '../../../../search/services/search.service';
import {
  applyTextSearch,
  applyTextSearchFailure,
  applyTextSearchSuccess,
  autocomplete,
  autocompleteFailure,
  autocompleteSuccess,
  loadInitialFilters,
  loadInitialFiltersFailure,
  loadInitialFiltersSuccess,
  requestBomExport,
  requestBomExportFailure,
  requestBomExportSuccess,
  resetFilters,
  search,
  searchFailure,
  searchSuccess,
  updatePaginationState,
} from '../../actions';
import { FilterItem } from '../../reducers/search/models';
import {
  getFiltersForSearchRequest,
  getTooManyResultsThreshold,
} from '../../selectors';

/**
 * Effects class for all tagging related actions which trigger side effects
 */
@Injectable()
export class SearchEffects {
  private readonly actions$ = inject(Actions);
  private readonly searchService = inject(SearchService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  /**
   * Receive initial filters
   */
  loadInitialFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInitialFilters),
      mergeMap(() =>
        this.searchService.getInitialFilters().pipe(
          map((items: FilterItem[]) => loadInitialFiltersSuccess({ items })),
          catchError((errorMessage) =>
            of(loadInitialFiltersFailure({ errorMessage }))
          )
        )
      )
    )
  );

  /**
   * Search
   */
  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(search),
      concatLatestFrom(() => [
        this.store.select(getFiltersForSearchRequest),
        this.store.select(getTooManyResultsThreshold),
      ]),
      map(([_action, items, threshold]) => ({ items, threshold })),
      mergeMap(({ items, threshold }) =>
        this.searchService.search(items).pipe(
          tap((searchResult) => {
            if (searchResult.count > 0 && searchResult.count <= threshold) {
              this.router.navigateByUrl(AppRoutePath.ResultsPath);
            }
          }),
          map((searchResult) => searchSuccess({ searchResult })),
          catchError((errorMessage) => of(searchFailure({ errorMessage })))
        )
      )
    )
  );

  /**
   * Text Search
   */
  applyTextSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(applyTextSearch),
      map((action) => action.textSearch),
      mergeMap((textSearch) =>
        this.searchService.textSearch(textSearch).pipe(
          map((searchResult) => applyTextSearchSuccess({ searchResult })),
          catchError((errorMessage) =>
            of(applyTextSearchFailure({ errorMessage }))
          )
        )
      )
    )
  );

  /**
   * Load initial filters due to reset
   *
   */
  resetFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetFilters),
      map(() => loadInitialFilters())
    )
  );

  /**
   * Get possible values for a form field
   *
   */
  autocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(autocomplete),
      mergeMap((action) =>
        this.searchService
          .autocomplete(action.searchFor, action.filter.name)
          .pipe(
            map((item) => autocompleteSuccess({ item })),
            catchError(() => of(autocompleteFailure({ item: action.filter })))
          )
      )
    )
  );

  /**
   * Unset pagination when navigating to the search page
   */
  resetPaginationState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(search),
      mergeMap((_) =>
        of(
          updatePaginationState({
            paginationState: undefined,
          })
        )
      )
    )
  );

  /**
   * Trigger BOMs export
   */
  requestBomExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestBomExport),
      map((action) => action.ids),
      mergeMap((referenceTypeIdentifiers) =>
        this.searchService.requestBomExport(referenceTypeIdentifiers).pipe(
          mergeMap(() => of(requestBomExportSuccess())),
          catchError((error: HttpErrorResponse) =>
            of(
              requestBomExportFailure({
                statusCode: error.status,
                errorMessage: error.message,
              })
            )
          )
        )
      )
    )
  );
}
